import styles from "./Navbar.module.css";
import UserIcon from "Utils/Assets/SVGs/UserIcon";
import LogoutIcon from "Utils/Assets/SVGs/LogoutIcon";
import BoxIcon from "Utils/Assets/SVGs/BoxIcon";
import LoginIcon from "Utils/Assets/SVGs/LoginIcon";
import UserAddIcon from "Utils/Assets/SVGs/UserAddIcon";
import Key from "Utils/Assets/SVGs/Key";
import { IUserMenuProps } from "../../../Model/NavBar.model";
import { useUserMenu } from "../../../Logic/Functions/Hooks/useUserMenu";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { Fragment, useState } from "react";

const UserMenu: React.FC<IUserMenuProps> = ({ className }) => {
  const authStore = UserAuthenticationStoreImplementation();
  const authState = authStore.authenticationState;
  const authUser = authState?.user;

  const firstName = authUser?.firstName ?? "";
  const lastName = authUser?.lastName ?? "";
  const email = authUser?.email ?? "";
  const token = authState?.token ?? "";
  const isLoggedIn = Boolean(token);


  const {
    open,
    toggle,
    triggerId,
    dropdownId,
    fullName,
    initials,
    goTo,
    onItemClick,
    btnRef,
    menuRef,
    panelStyle,
  } = useUserMenu({ firstName, lastName });

  const auth = new AuthenticationService();

  const [logoutLoading, setLogoutLoading] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const handleSignOut = onItemClick(() => {
    auth.logout({
      setApiError: setLogoutError,
      setLoading: setLogoutLoading,
      handleUserData: authStore.logUserOut,
      token,
      isProtectedRoute: true,
    });
  });

  return (
    <div className={`${styles.userWrapper} ${className ?? ""}`} data-testid="user-menu">
      <button
        id={triggerId}
        type="button"
        ref={btnRef}
        className={styles.userTrigger}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={dropdownId}
        aria-label={isLoggedIn ? fullName || "Account" : "Sign in"}
        data-testid="user-menu-trigger"
        title={isLoggedIn ? fullName || "Account" : "Sign in"}
      >
        {isLoggedIn ? (
          <span className={styles.avatarCircle} aria-hidden="true">
            <span className={styles.avatarInitials}>{initials}</span>
          </span>
        ) : (
          <UserIcon className={styles.userIcon} />
        )}
      </button>

      {open && (
        <div
          id={dropdownId}
          role="menu"
          aria-labelledby={triggerId}
          ref={menuRef}
          className={styles.userDropdown}
          style={panelStyle}
          data-testid="user-menu-dropdown"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isLoggedIn ? (
            <Fragment>
              <div className={styles.userCard} role="none">
                <div className={styles.userAvatar} aria-hidden="true">
                  <span className={styles.initials}>{initials}</span>
                </div>
                <div className={styles.userMeta}>
                  <div className={styles.userFullName} data-testid="user-menu-name">
                    {fullName || "User"}
                  </div>
                  {email && <div className={styles.userEmail}>{email}</div>}
                </div>
              </div>

              <hr className={styles.userDivider} />

              <button
                role="menuitem"
                className={styles.userItem}
                onClick={goTo("/auth/profile")}
                data-testid="user-menu-profile"
              >
                <UserIcon className={styles.userItemIcon} />
                <span>Profile</span>
              </button>

              <button
                role="menuitem"
                className={styles.userItem}
                onClick={goTo("/my-orders")}
                data-testid="user-menu-orders"
              >
                <BoxIcon className={styles.userItemIcon} />
                <span>My orders</span>
              </button>

              <button
                role="menuitem"
                className={styles.userItem}
                onClick={goTo("/auth/change-password")}
                data-testid="user-menu-change-password"
              >
                <Key className={styles.userItemIcon} />
                <span>Change password</span>
              </button>

              <hr className={styles.userDivider} />

              {logoutError && (
                <div className={styles.userError} role="alert">
                  {logoutError}
                </div>
              )}

              <button
                role="menuitem"
                className={`${styles.userItem} ${styles.danger}`}
                onClick={handleSignOut}
                data-testid="user-menu-signout"
                disabled={logoutLoading}
                aria-disabled={logoutLoading}
              >
                <LogoutIcon className={styles.userItemIcon} />
                <span>{logoutLoading ? "Logging out..." : "Log out"}</span>
              </button>
            </Fragment>
          ) : (
            <Fragment>
              <button
                role="menuitem"
                className={styles.userItem}
                onClick={goTo("/auth/login")}
                data-testid="user-menu-login"
              >
                <LoginIcon className={styles.userItemIcon} />
                <span>Login</span>
              </button>

              <button
                role="menuitem"
                className={styles.userItem}
                onClick={goTo("/auth/signup")}
                data-testid="user-menu-register"
              >
                <UserAddIcon className={styles.userItemIcon} />
                <span>Register</span>
              </button>
            </Fragment>
          )}
        </div>
      )}
    </div>
  );
};

export default UserMenu;
